/**
 * Mailing API client.
 *
 * Thin promise wrapper over /compuzz/rest/v2/mailing. Exported directly rather
 * than through reqres handlers: the feature has ~30 endpoints and one handler
 * each would be noise — the views just require this module and call it.
 *
 * Every method resolves with the parsed body and rejects with the jqXHR, so
 * callers can read `xhr.responseJSON.error` for the backend's message.
 */
define('entities/mailing',[
  'module',
  'jquery',
  'underscore',
  'settings'
], function (
  module,
  $,
  _,
  Settings
) {
  'use strict';

  /**
   * The settings endpoint speaks the snake_case keys the standalone app used,
   * so a settings dump from it replays unchanged. Views work in camelCase and
   * the translation happens here, in one place.
   */
  var SETTING_KEYS = {
    smtpHost: 'smtp_host',
    smtpPort: 'smtp_port',
    smtpSecure: 'smtp_secure',
    smtpUser: 'smtp_user',
    smtpPassword: 'smtp_password',
    smtpFromName: 'smtp_from_name',
    queueMaxPerHour: 'queue_max_per_hour',
    queueDelaySeconds: 'queue_delay_seconds',
    openaiApiKey: 'openai_api_key',
    personalisedParallel: 'personalised_parallel',
    sendWindowTimezone: 'send_window_timezone'
  };

  /**
   * Languages that can have their own sender. Each adds two settings,
   * smtp_from_name_<LANG> / smtp_from_email_<LANG>, exposed to the views as
   * fromNameDE / fromEmailDE…
   */
  var SENDER_LANGUAGES = ['DE', 'FR', 'NL', 'DK', 'ES', 'IT', 'EN'];
  _.each(SENDER_LANGUAGES, function (language) {
    SETTING_KEYS['fromName' + language] = 'smtp_from_name_' + language;
    SETTING_KEYS['fromEmail' + language] = 'smtp_from_email_' + language;
  });

  function fromWireSettings(wire) {
    var settings = {};
    _.each(SETTING_KEYS, function (wireKey, localKey) {
      settings[localKey] = (wire || {})[wireKey];
    });
    return settings;
  }

  function toWireSettings(settings) {
    var wire = {};
    _.each(SETTING_KEYS, function (wireKey, localKey) {
      if (settings[localKey] !== undefined) {
        wire[wireKey] = String(settings[localKey]);
      }
    });
    return wire;
  }

  function url(path, params) {
    return Settings.url('compuzz2', 'v2/mailing/' + path, params);
  }

  function request(method, path, params, body) {
    var defer = $.Deferred();
    var options = {
      type: method,
      url: url(path, params),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    };
    if (body !== undefined && body !== null) {
      options.contentType = 'application/json';
      options.data = JSON.stringify(body);
    }
    $.ajax(options);
    return defer.promise();
  }

  function get(path, params) { return request('GET', path, params); }
  function post(path, body, params) { return request('POST', path, params, body === undefined ? {} : body); }
  function put(path, body) { return request('PUT', path, null, body); }
  function del(path) { return request('DELETE', path); }

  var defaultImagePrompt = null;

  var API = {

    //----------Subscribers--------------------------------------------------//

    subscribers: function (params) { return get('subscribers', params); },
    subscriber: function (secId) { return get('subscribers/' + secId); },
    createSubscriber: function (data) { return post('subscribers', data); },
    updateSubscriber: function (secId, data) { return put('subscribers/' + secId, data); },
    deleteSubscriber: function (secId) { return del('subscribers/' + secId); },
    subscriberHistory: function (secId) { return get('subscribers/' + secId + '/history'); },
    /** Every mailing email sent to a customer (all its addresses), newest first. */
    customerHistory: function (endUserSecId) { return get('customers/' + endUserSecId + '/history'); },
    subscriberClicks: function (secId, sendLogSecId) {
      return get('subscribers/' + secId + '/history/' + sendLogSecId + '/clicks');
    },
    syncSubscribers: function () { return post('subscribers/sync'); },

    //----------Segments-----------------------------------------------------//

    segments: function () { return get('segments'); },
    segment: function (secId) { return get('segments/' + secId); },
    segmentFields: function () { return get('segments/fields'); },
    /** `limit` (optional): how many matching contacts to return — 8 by default, the backend caps at 5000. */
    previewSegment: function (filters, limit) {
      return post('segments/preview', { filters: filters }, limit ? { limit: limit } : null);
    },
    segmentSubscribers: function (secId, limit) {
      return get('segments/' + secId + '/subscribers', { limit: limit || 500 });
    },
    createSegment: function (data) { return post('segments', data); },
    updateSegment: function (secId, data) { return put('segments/' + secId, data); },
    deleteSegment: function (secId) { return del('segments/' + secId); },
    /** Per segment: contacts from the synced contact list vs from the customer file (dry run of the switch). */
    audienceCheck: function () { return get('segments/audience-check'); },

    //----------Templates----------------------------------------------------//

    templates: function () { return get('templates'); },
    template: function (secId) { return get('templates/' + secId); },
    createTemplate: function (data) { return post('templates', data); },
    updateTemplate: function (secId, data) { return put('templates/' + secId, data); },
    deleteTemplate: function (secId) { return del('templates/' + secId); },
    duplicateTemplate: function (secId) { return post('templates/' + secId + '/duplicate'); },
    /** Resolves with { html }: the template as one contact would see it (no contact = tags left visible). */
    previewTemplate: function (secId, subscriberSecId) {
      return get('templates/' + secId + '/preview',
        subscriberSecId ? { subscriberSecId: subscriberSecId } : null);
    },
    sendTestEmail: function (data) { return post('templates/send-test', data); },
    /**
     * Portable file, in the standalone zaprinta-mailings format (snake_case keys,
     * design as an object) — not the shape the rest of this API uses.
     */
    exportTemplate: function (secId) { return get('templates/' + secId + '/export'); },
    /** `file` is the parsed export. Resolves with { template, warnings }. */
    importTemplate: function (file) { return post('templates/import', file); },

    //----------Image library------------------------------------------------//

    images: function () { return get('images'); },
    /** Multipart, one file per call; resolves with the stored image ({ url, width, height… }). */
    uploadImage: function (file) {
      var defer = $.Deferred();
      var formData = new FormData();
      formData.append('file', file, file.name);
      $.ajax({
        type: 'POST',
        url: url('images'),
        data: formData,
        processData: false,
        contentType: false,
        success: function (data) { defer.resolve(data); },
        error: function (xhr) { defer.reject(xhr); }
      });
      return defer.promise();
    },
    deleteImage: function (secId) { return del('images/' + secId); },

    //----------Saved rows (template editor row library)-----------------------//

    savedRows: function () { return get('saved-rows'); },
    createSavedRow: function (data) { return post('saved-rows', data); },
    renameSavedRow: function (secId, label) { return put('saved-rows/' + secId, { label: label }); },
    deleteSavedRow: function (secId) { return del('saved-rows/' + secId); },

    //----------Campaigns----------------------------------------------------//

    campaigns: function () { return get('campaigns'); },
    campaign: function (secId) { return get('campaigns/' + secId); },
    campaignStats: function (secId) { return get('campaigns/' + secId + '/stats'); },
    createCampaign: function (data) { return post('campaigns', data); },
    updateCampaign: function (secId, data) { return put('campaigns/' + secId, data); },
    deleteCampaign: function (secId) { return del('campaigns/' + secId); },
    duplicateCampaign: function (secId) { return post('campaigns/' + secId + '/duplicate'); },
    sendCampaign: function (secId) { return post('campaigns/' + secId + '/send'); },
    scheduleCampaign: function (secId, timestamp) {
      return post('campaigns/' + secId + '/schedule', { scheduledAt: timestamp });
    },
    unscheduleCampaign: function (secId) { return post('campaigns/' + secId + '/unschedule'); },
    /**
     * { sendDays, sendStartTime, sendEndTime } — unlike updateCampaign, allowed
     * while the campaign is sending; the emails still in the outbox follow.
     */
    updateCampaignSendWindow: function (secId, sendWindow) {
      return put('campaigns/' + secId + '/send-window', sendWindow);
    },

    //----------Personalised campaigns---------------------------------------//

    personalisedCampaigns: function () { return get('personalised-campaigns'); },
    personalisedCampaign: function (secId) { return get('personalised-campaigns/' + secId); },
    createPersonalisedCampaign: function (data) { return post('personalised-campaigns', data); },
    updatePersonalisedCampaign: function (secId, data) { return put('personalised-campaigns/' + secId, data); },
    /** The built-in image prompt ({prompt}); fetched once per page load. */
    defaultPersonalisedImagePrompt: function () {
      if (!defaultImagePrompt) {
        defaultImagePrompt = get('personalised-campaigns/default-image-prompt');
        defaultImagePrompt.fail(function () { defaultImagePrompt = null; });
      }
      return defaultImagePrompt;
    },
    /** The campaign's own image prompt, draft only; '' = back to the built-in one. */
    updatePersonalisedImagePrompt: function (secId, prompt) {
      return put('personalised-campaigns/' + secId + '/image-prompt', { imagePrompt: prompt || '' });
    },
    /**
     * PIM product behind {{proofsheet_url}}: { proofsheetProductRef (reseller code),
     * proofsheetProductName, proofsheetConfiguration (JSON string) }; a blank ref removes it.
     * Editable at any stage, unlike the brief.
     */
    configurePersonalisedProofsheet: function (secId, data) {
      return put('personalised-campaigns/' + secId + '/proofsheet', data);
    },
    deletePersonalisedCampaign: function (secId) { return del('personalised-campaigns/' + secId); },
    duplicatePersonalisedCampaign: function (secId) {
      return post('personalised-campaigns/' + secId + '/duplicate');
    },
    generatePersonalisedCampaign: function (secId) {
      return post('personalised-campaigns/' + secId + '/generate');
    },
    personalisedItems: function (secId) { return get('personalised-campaigns/' + secId + '/items'); },
    personalisedStats: function (secId) { return get('personalised-campaigns/' + secId + '/stats'); },
    /** `itemIds` are the items' plain ids, not their secIds — a selection can run to hundreds. */
    sendPersonalisedItems: function (secId, itemIds) {
      return post('personalised-campaigns/' + secId + '/send', { itemIds: itemIds });
    },
    /** Every failed image of the campaign back in the generation queue; resolves with { requeued }. */
    /** Every image still to send that an older prompt made, regenerated with the current one. */
    regeneratePersonalisedOlderPrompt: function (secId) {
      return post('personalised-campaigns/' + secId + '/regenerate-older-prompt');
    },
    /**
     * Recipients whose address field lists several addresses: their failed or waiting
     * emails cancelled, each split into one recipient per address with its image.
     * Resolves with { cancelled, split, created, skipped }.
     */
    /** { count } of segment contacts not yet recipients of the campaign (runs the segment: not on every refresh). */
    personalisedNewContacts: function (secId) {
      return get('personalised-campaigns/' + secId + '/new-contacts');
    },
    /** Adds them, images queued with the current brief, nothing sent; resolves with { added }. */
    addPersonalisedNewContacts: function (secId) {
      return post('personalised-campaigns/' + secId + '/add-new-contacts');
    },
    fixPersonalisedMultiAddress: function (secId) {
      return post('personalised-campaigns/' + secId + '/fix-multi-address');
    },
    retryAllPersonalisedFailed: function (secId) {
      return post('personalised-campaigns/' + secId + '/retry-failed');
    },
    retryPersonalisedItem: function (secId, itemSecId) {
      return post('personalised-campaigns/' + secId + '/items/' + itemSecId + '/retry');
    },
    regeneratePersonalisedItem: function (secId, itemSecId) {
      return post('personalised-campaigns/' + secId + '/items/' + itemSecId + '/regenerate');
    },
    /** Only items still waiting in the generation queue can be removed. */
    removePersonalisedItem: function (secId, itemSecId) {
      return del('personalised-campaigns/' + secId + '/items/' + itemSecId);
    },
    clearPersonalisedQueue: function (secId) {
      return del('personalised-campaigns/' + secId + '/queue');
    },
    previewPersonalisedItem: function (secId, itemSecId) {
      return get('personalised-campaigns/' + secId + '/items/' + itemSecId + '/preview');
    },
    /** Slow by nature (30–90 s): the backend waits for OpenAI before answering. */
    testPersonalisedImage: function (secId, websiteUrl) {
      return post('personalised-campaigns/' + secId + '/test-image', { websiteUrl: websiteUrl });
    },

    //----------Workflows----------------------------------------------------//

    workflows: function () { return get('workflows'); },
    workflow: function (secId) { return get('workflows/' + secId); },
    workflowEnrollments: function (secId) { return get('workflows/' + secId + '/enrollments'); },
    createWorkflow: function (data) { return post('workflows', data); },
    updateWorkflow: function (secId, data) { return put('workflows/' + secId, data); },
    deleteWorkflow: function (secId) { return del('workflows/' + secId); },
    activateWorkflow: function (secId) { return post('workflows/' + secId + '/activate'); },
    pauseWorkflow: function (secId) { return post('workflows/' + secId + '/pause'); },

    //----------Queue--------------------------------------------------------//

    queue: function (params) { return get('queue', params); },
    queueStats: function () { return get('queue/stats'); },
    retryFailed: function () { return post('queue/retry-failed'); },
    cancelQueueItem: function (secId) { return del('queue/' + secId); },
    /** { total } for a status filter ('' = all): drives the outbox pager. */
    queueCount: function (params) { return get('queue/count', params); },
    /** Cancels the given pending emails; resolves with { cancelled }. */
    cancelQueueItems: function (secIds) { return post('queue/cancel', { secIds: secIds }); },
    /** Cancels everything still pending in the account's outbox; resolves with { cancelled }. */
    cancelAllPendingQueueItems: function () { return post('queue/cancel', { allPending: true }); },
    /** Cancels every failed email of the account (out of "Retry all failed"); resolves with { cancelled }. */
    cancelAllFailedQueueItems: function () { return post('queue/cancel', { allFailed: true }); },
    /** A cancelled email back in the outbox, as it was rendered. */
    resendQueueItem: function (secId) { return post('queue/' + secId + '/resend'); },

    //----------Settings-----------------------------------------------------//

    settings: function () {
      return get('settings').then(fromWireSettings);
    },
    updateSettings: function (data) {
      return put('settings', toWireSettings(data)).then(fromWireSettings);
    },
    testSmtp: function () { return post('settings/test-smtp'); },
    /**
     * Hands the backend this environment's locale → webshop map (Settings
     * 'webshopUrl'): it has none of its own, and needs it to redirect a customer
     * to the right webshop's proofsheet. Idempotent; sent on entering the module.
     */
    pushWebshopUrls: function (urls) {
      return put('settings/webshop-urls', { webshopUrls: JSON.stringify(urls || {}) });
    },
    senderLanguages: function () { return SENDER_LANGUAGES.slice(); },

    //----------Helpers------------------------------------------------------//

    /** Backend error message, or a generic fallback. */
    errorMessage: function (xhr) {
      var status = xhr && xhr.status;
      var body = xhr && xhr.responseJSON;
      var text = xhr && !body && xhr.responseText ? String(xhr.responseText).substring(0, 200) : '';
      if (window.console) {
        window.console.error('[mailing] request failed', status, xhr && xhr.statusText, body || text);
      }
      if (body && (body.error || body.message)) {
        return body.error || body.message;
      }
      if (status === 403) {
        return 'You are not allowed to do this';
      }
      if (status === 0) {
        return 'Cannot reach the server (network error or request blocked)';
      }
      return 'Something went wrong (HTTP ' + status + (text ? ': ' + text : '') + ')';
    }
  };

  module.exports = API;
});

